import type { UnitStatReportViewModel } from 'src/api/types/UnitStatReportViewModel';

import Grid from '@mui/material/Grid';
import { Box, Divider, Typography } from '@mui/material';

import { PolicyStatusChips } from 'src/sections/employer-data/shared/components/status-chips';
import {
  formatDate,
  formatDollarAmount,
  mapPlanTypeCodeToDescription,
  mapCorrectionTypeCodeToDescription,
  mapEstimatedAuditCodeToDescription,
} from 'src/sections/employer-data/shared/utils/policyUtils';

import { InfoBox } from '../../../../shared/components/info-box';

type UnitStatHeaderProps = {
  data: UnitStatReportViewModel | undefined;
};

export default function UnitStatHeader({ data }: UnitStatHeaderProps) {
  return (
    <Box sx={{ paddingTop: 2 }}>
      <Typography
        sx={{ color: 'text.primary' }}
        data-testid="primary-unit-stat-report-title"
        variant="subtitle1"
      >
        Policy info
      </Typography>

      <Grid container spacing={2} columns={4}>
        <Grid size={{ xs: 4, md: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3 }}>
            <Typography
              component="span"
              sx={{ typography: 'body2', color: 'text.secondary' }}
              data-testid="primary-insured-name-label"
            >
              Insured&apos;s name
            </Typography>

            <Typography
              component="span"
              sx={{ typography: 'subtitle2', color: 'text.primary' }}
              data-testid="primary-insured-name-value"
            >
              {data?.insuredName || ''}
            </Typography>
            <Box sx={{ mt: 1, ml: -1 }}>
              <PolicyStatusChips isMultiStatePolicy={data?.multistateIndicator} />
            </Box>
          </Box>
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Address"
            value={data?.insuredAddress || ''}
            testIdLabel="mailing-address-label"
            testIdValue="mailing-address-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Received date"
            value={formatDate(data?.receivedDate) || ''}
            testIdLabel="received-date-label"
            testIdValue="received-date-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Submission ID"
            value={data?.submissionId || ''}
            testIdLabel="submission-id-label"
            testIdValue="submission-id-value"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} columns={4}>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Correction type"
            value={mapCorrectionTypeCodeToDescription(
              data?.correctionTypeCodeNavigation?.correctionTypeCode
            )}
            testIdLabel="correction-type-label"
            testIdValue="correction-type-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Replacement indicator"
            value={data?.replacementReportIndicator ? 'Yes' : 'No'}
            testIdLabel="replacement-indicator-label"
            testIdValue="replacement-indicator-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Exposure state"
            value={data?.exposureStateCodeNavigation?.stateName || ''}
            testIdLabel="exposure-state-label"
            testIdValue="exposure-state-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="State effective date"
            value={formatDate(data?.stateEffectiveDate) || ''}
            testIdLabel="state-effective-date-label"
            testIdValue="state-effective-date-value"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} columns={4}>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Risk ID"
            value={data?.riskNumber || ''}
            testIdLabel="risk-id-label"
            testIdValue="risk-id-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Accepted date"
            value={formatDate(data?.acceptedDate) || ''}
            testIdLabel="accepted-date-label"
            testIdValue="accepted-date-value"
          />
        </Grid>
      </Grid>

      <Divider
        sx={{
          borderStyle: 'dashed',
          paddingTop: '2.5rem',
        }}
      />

      <Typography
        sx={{ color: 'text.primary', paddingTop: '2.5rem' }}
        data-testid="policy-type-id-title"
        variant="subtitle1"
      >
        Policy type ID
      </Typography>

      <Grid container spacing={2} columns={4}>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Type coverage"
            value={data?.coverageTypeCodeNavigation?.coverageTypeDescription || ''}
            testIdLabel="type-coverage-label"
            testIdValue="type-coverage-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Market"
            value={mapPlanTypeCodeToDescription(data?.planTypeCode)}
            testIdLabel="market-label"
            testIdValue="market-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Non standard indicator"
            value={data?.nonStandardCodeNavigation?.nonStandardDescription || ''}
            testIdLabel="non-standard-indicator-label"
            testIdValue="non-standard-indicator-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Audit complete"
            value={mapEstimatedAuditCodeToDescription(data?.estimatedAuditCode)}
            testIdLabel="estimated-audit-label"
            testIdValue="estimated-audit-value"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} columns={4}>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Losses subject to deductible"
            value={
              data?.lossesSubjectDeductibleCodeNavigation?.lossesSubjectDeductibleDescription || ''
            }
            testIdLabel="losses-subject-deductible-label"
            testIdValue="losses-subject-deductible-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Deductible amount - Claim/Accident"
            value={formatDollarAmount(data?.deductibleClaimAccident)}
            testIdLabel="deductible-amount-claim-accident-label"
            testIdValue="deductible-amount-claim-accident-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Deductible amount - Aggregate"
            value={formatDollarAmount(data?.deductibleAggregate)}
            testIdLabel="deductible-amount-aggregate-label"
            testIdValue="deductible-amount-aggregate-value"
          />
        </Grid>
        <Grid size={{ xs: 4, md: 1 }}>
          <InfoBox
            label="Basis of deductible calculation"
            value={
              data?.basisDeductibleCalculationCodeNavigation
                ?.basisDeductibleCalculationDescription || ''
            }
            testIdLabel="basis-of-deductible-calculation-label"
            testIdValue="basis-of-deductible-calculation-value"
          />
        </Grid>
      </Grid>
    </Box>
  );
}
